import { saveAs } from 'file-saver';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { centsToXlsxNumber, applyCurrencyFormat } from '../../../helpers/excelExportHelpers';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';

/**
 * Export manifest report data to XLSX file
 * @param {object} reportData - The report data from the API
 * @param {string} dateFrom - Start date string
 * @param {string} dateTo - End date string
 * @param {boolean} includeAllFields - Whether to include all fields in the export
 */
export const exportManifestReportToXlsx = async (
    reportData,
    dateFrom,
    dateTo,
    includeAllFields,
) => {
    const XLSX = await import('xlsx');
    const fileType =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    // Build the data array for the sheet
    const sheetData = [];

    // Add header with date range and total
    sheetData.push(['Manifest Report']);
    sheetData.push([
        `Date Range: ${new Date(dateFrom + 'T12:00:00').toLocaleDateString()} - ${new Date(dateTo + 'T12:00:00').toLocaleDateString()}`,
    ]);
    sheetData.push([`Total Records: ${reportData.total || 0}`]);

    // Add tier summary if groups available
    if (reportData.groups && reportData.groups.length > 0) {
        sheetData.push([]);
        for (const group of reportData.groups) {
            const breakdown = (group.ticketOptionBreakdown || [])
                .map((opt) => `${opt.count} ${opt.name}`)
                .join(', ');
            sheetData.push([
                `${group.ticketTypeName}: ${group.bookingCount} bookings, ${group.customerCount} pax — ${breakdown} — ${group.checkedInCount} checked in${group.notCheckedInCount > 0 ? `, ${group.notCheckedInCount} not checked in` : ''} — ${formatCentsToUsd(group.totalPrice)}${group.unpaidTotal > 0 ? ` — ${formatCentsToUsd(group.unpaidTotal)} unpaid` : ''}`,
            ]);
        }
    }

    sheetData.push([]); // Empty row

    // Base columns
    const headers = [
        'Booking ID',
        'Booking Status',
        'Contact',
        'Voucher',
        'Customers',
        'Checked In',
        'Seating Group',
        'Seats',
        'Show Date',
        'Created at',
        'Affiliate',
        'Total',
        'Unpaid',
        'Notes',
    ];

    // Collect all unique custom field labels across all items for dynamic columns
    let customFieldLabels = [];
    if (includeAllFields) {
        const labelSet = new Set();
        for (const item of reportData.items) {
            if (Array.isArray(item.customFields)) {
                for (const cf of item.customFields) {
                    if (cf.label) labelSet.add(cf.label);
                }
            }
        }
        customFieldLabels = Array.from(labelSet);
    }

    // Extra columns when all fields are included
    if (includeAllFields) {
        headers.push(
            'Show',
            'Email',
            'Phone',
            // Dynamic custom field columns
            ...customFieldLabels,
            'Language',
            'Source',
        );
    }

    const buildRow = (item) => {
        const row = [
            item.bookingId,
            item.status || '',
            item.contact,
            item.voucher || '',
            item.customers,
            item.isCheckedIn ? 'Yes' : 'No',
            item.seatingGroup || '',
            item.seats,
            item.availability || '',
            item.bookingDate ? formatDateTimeInVenueTz(item.bookingDate) : '',
            item.affiliateName || '',
            centsToXlsxNumber(item.totalPrice),
            item.unpaidAmount ? centsToXlsxNumber(item.unpaidAmount) : '',
            item.checkinNote || item.agentNotes || '',
        ];
        if (includeAllFields) {
            const cfMap = {};
            if (Array.isArray(item.customFields)) {
                for (const cf of item.customFields) {
                    cfMap[cf.label] = cf.value;
                }
            }
            row.push(
                item.showTitle || '',
                item.email || '',
                item.phone || '',
                ...customFieldLabels.map((label) => cfMap[label] || ''),
                item.language || '',
                item.integrationSource || '',
            );
        }
        return row;
    };

    // Flat export (consistent with other report exports)
    sheetData.push(headers);
    reportData.items.forEach((item) => sheetData.push(buildRow(item)));

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Set column widths
    const colWidths = [
        { wch: 12 }, // Booking ID
        { wch: 12 }, // Booking Status
        { wch: 22 }, // Contact
        { wch: 12 }, // Voucher
        { wch: 30 }, // Customers
        { wch: 12 }, // Checked In
        { wch: 25 }, // Seating Group
        { wch: 30 }, // Seats
        { wch: 20 }, // Show Date
        { wch: 15 }, // Created at
        { wch: 25 }, // Affiliate
        { wch: 12 }, // Total
        { wch: 12 }, // Unpaid
        { wch: 40 }, // Notes
    ];

    if (includeAllFields) {
        colWidths.push(
            { wch: 25 }, // Show
            { wch: 30 }, // Email
            { wch: 18 }, // Phone
            ...customFieldLabels.map(() => ({ wch: 25 })),
            { wch: 10 }, // Language
            { wch: 15 }, // Source
        );
    }

    ws['!cols'] = colWidths;
    // Apply currency format to Total (11) and Unpaid (12) columns
    applyCurrencyFormat(XLSX, ws, sheetData, [11, 12]);

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Manifest Report');

    // Generate file
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const file = new Blob([excelBuffer], { type: fileType });

    // Generate filename with date range
    const filename = `manifest-report-${dateFrom}-to-${dateTo}${fileExtension}`;
    saveAs(file, filename);
};

export default exportManifestReportToXlsx;
